import { useMemo } from 'react';

import { equals } from 'ramda';

import { createOwnedTabScopedRegistry } from './tab-scoped-registry';

export interface EditorFindState {
  matchAccents: boolean;
  matchCase: boolean;
  replace: string;
  search: string | null;
}

export interface EditorFindStateActions {
  clearAdvancedSearchOptions: () => void;
  clearSearch: () => void;
  setMatchAccents: (matchAccents: boolean) => void;
  setMatchCase: (matchCase: boolean) => void;
  setReplace: (replace: string) => void;
  setSearch: (search: string) => void;
}

const initialEditorFindState: EditorFindState = {
  matchAccents: false,
  matchCase: false,
  replace: '',
  search: null,
};

const findStateRegistry = createOwnedTabScopedRegistry({
  areEqual: equals,
  initialState: initialEditorFindState,
  useTabIdErrorMessage: 'useEditorFindStateActions must be used within a find state scope.',
});

export const EditorFindStateProvider = findStateRegistry.Provider;
export const EditorFindStateScope = findStateRegistry.Scope;

export function useMaybeEditorFindState(): EditorFindState | null {
  const tabId = findStateRegistry.useMaybeTabId();
  const state = findStateRegistry.useSelector(state => state, equals);
  if (!tabId) return null;

  return state;
}

export function useEditorFindState(): EditorFindState {
  return useEditorFindStateSelector(state => state, equals);
}

export function useEditorFindStateSelector<TValue>(
  selector: (state: EditorFindState) => TValue,
  isEqual?: (left: TValue, right: TValue) => boolean
): TValue {
  return findStateRegistry.useSelector(selector, isEqual);
}

export function useEditorFindStateActions(): EditorFindStateActions {
  const tabId = findStateRegistry.useTabId();
  return useMemo(() => createFindStateActions(tabId), [tabId]);
}

export function getEditorFindStateForTabId(tabId: UUID | null): EditorFindState | null {
  return findStateRegistry.getForTabId(tabId);
}

export function updateEditorFindStateForTabId(
  tabId: UUID,
  updater: (state: EditorFindState) => EditorFindState
): void {
  findStateRegistry.updateForTabId(tabId, updater);
}

function createFindStateActions(tabId: UUID): EditorFindStateActions {
  return {
    clearAdvancedSearchOptions: () => {
      updateEditorFindStateForTabId(tabId, state => ({
        ...state,
        matchAccents: false,
        matchCase: false,
        replace: '',
      }));
    },
    clearSearch: () => {
      updateEditorFindStateForTabId(tabId, () => initialEditorFindState);
    },
    setMatchAccents: matchAccents => {
      updateEditorFindStateForTabId(tabId, state => ({ ...state, matchAccents }));
    },
    setMatchCase: matchCase => {
      updateEditorFindStateForTabId(tabId, state => ({ ...state, matchCase }));
    },
    setReplace: replace => {
      updateEditorFindStateForTabId(tabId, state => ({ ...state, replace }));
    },
    setSearch: search => {
      updateEditorFindStateForTabId(tabId, state => ({ ...state, search }));
    },
  };
}
